import { useEffect, useRef, useState, useCallback } from "react";
import axios from "axios";
import { RefreshCw, LineChart, Loader2, AlertTriangle } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TIMEFRAMES = [
  { key: "1w", label: "1W" },
  { key: "1d", label: "1D" },
  { key: "4h", label: "4H" },
  { key: "1h", label: "1H" },
];

const ICT_TIMEFRAMES = [...TIMEFRAMES, { key: "15m", label: "15M" }, { key: "5m", label: "5M" }, { key: "1m", label: "1M" }];

const ZONE_STYLE = {
  weekly: { color: "#a855f7", label: "Weekly", code: "W" },
  daily: { color: "#3b82f6", label: "Daily", code: "D" },
  "4h": { color: "#f97316", label: "4H", code: "4H" },
  "1h": { color: "#14b8a6", label: "1H", code: "1H" },
};

const DEFAULT_ACTIVE_ZONES = { weekly: true, daily: true, "4h": false, "1h": false };

/**
 * TradingView-style candlestick chart (Lightweight Charts) with the 4
 * multi-timeframe zone overlays (Weekly/Daily/4H/1H swing bands) and the
 * current AI trade plan (entry/stop/TP) always drawn on top, regardless of
 * which candle timeframe is selected.
 *
 * `lightweight-charts` is loaded via dynamic import (not a static top-level
 * import) so it never ships in the main app bundle - it's fetched only when
 * a user actually opens a Chart tab. This keeps any charting-library/canvas
 * incompatibility on a given device from being able to crash the whole app
 * (login screen, dashboard, etc.) - the failure is contained to this card.
 */
const AuraLightweightChart = ({ asset, symbol, decimals = 5, label, overrideTradePlan, defaultTimeframe = "1d", candleSource = "alpha_vantage", showZoneToggles = true }) => {
  const containerRef = useRef(null);
  const chartRef = useRef(null);
  const seriesRef = useRef(null);
  const priceLinesRef = useRef([]);
  const markersRef = useRef(null);
  const lastCandlesRef = useRef([]);

  const [ready, setReady] = useState(false);
  const [chartError, setChartError] = useState(null);
  const [timeframe, setTimeframe] = useState(defaultTimeframe);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [meta, setMeta] = useState(null); // { source, price_fetched_at, zones, trade_plan }
  const [activeZones, setActiveZones] = useState(DEFAULT_ACTIVE_ZONES);
  const [showPlan, setShowPlan] = useState(true);
  const activeZonesRef = useRef(DEFAULT_ACTIVE_ZONES);
  const showPlanRef = useRef(true);
  const overrideTradePlanRef = useRef(overrideTradePlan);

  useEffect(() => {
    if (!containerRef.current || chartRef.current) return;
    let cancelled = false;

    import("lightweight-charts")
      .then(({ createChart, CandlestickSeries, ColorType, createSeriesMarkers }) => {
        if (cancelled || !containerRef.current) return;
        const chart = createChart(containerRef.current, {
          layout: { background: { type: ColorType.Solid, color: "transparent" }, textColor: "#94a3b8" },
          grid: { vertLines: { color: "#1e293b" }, horzLines: { color: "#1e293b" } },
          width: containerRef.current.clientWidth,
          height: 340,
          timeScale: { timeVisible: true, secondsVisible: false },
          localization: { locale: "en-US" },
        });
        const series = chart.addSeries(CandlestickSeries, {
          upColor: "#22c55e",
          downColor: "#ef4444",
          borderVisible: true,
          borderUpColor: "#16a34a",
          borderDownColor: "#dc2626",
          wickUpColor: "#22c55e",
          wickDownColor: "#ef4444",
          wickVisible: true,
          priceFormat: { type: "price", precision: decimals, minMove: 1 / 10 ** decimals },
        });
        chartRef.current = chart;
        seriesRef.current = series;
        markersRef.current = createSeriesMarkers(series, []);
        setReady(true);
      })
      .catch((err) => {
        console.error("Failed to load charting library:", err);
        if (!cancelled) setChartError("Chart unavailable on this device/browser.");
      });

    const handleResize = () => {
      if (containerRef.current && chartRef.current) {
        chartRef.current.applyOptions({ width: containerRef.current.clientWidth });
      }
    };
    window.addEventListener("resize", handleResize);
    return () => {
      cancelled = true;
      window.removeEventListener("resize", handleResize);
      if (chartRef.current) chartRef.current.remove();
      chartRef.current = null;
      seriesRef.current = null;
      markersRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const drawZonesAndPlan = useCallback((zones, tradePlan, zoneFilter, planFilter) => {
    const series = seriesRef.current;
    if (!series) return;
    priceLinesRef.current.forEach((pl) => series.removePriceLine(pl));
    priceLinesRef.current = [];

    Object.entries(zones || {}).forEach(([tf, zone]) => {
      if (!zone || !zoneFilter[tf]) return;
      const style = ZONE_STYLE[tf];
      [["high", zone.high], ["low", zone.low]].forEach(([side, price]) => {
        const line = series.createPriceLine({
          price,
          color: style.color,
          lineWidth: 1,
          lineStyle: 2,
          axisLabelVisible: true,
          title: `${style.code} ${side === "high" ? "\u25B2" : "\u25BC"}`,
        });
        priceLinesRef.current.push(line);
      });
    });

    if (planFilter && tradePlan && tradePlan.bias !== "NEUTRAL" && tradePlan.bias !== "N/A") {
      // POI (Point of Interest) zone: the actual buy/sell band the confluence
      // factors are clustered around - drawn as two dotted amber lines framing
      // the entry area, so the "zone" is visible on the chart, not just a
      // single entry line.
      if (tradePlan.entryZone && tradePlan.entryZone.low != null && tradePlan.entryZone.high != null) {
        [["POI Zone Top", tradePlan.entryZone.high], ["POI Zone Bottom", tradePlan.entryZone.low]].forEach(([title, price]) => {
          const line = series.createPriceLine({
            price,
            color: "#f59e0b",
            lineWidth: 1,
            lineStyle: 3,
            axisLabelVisible: true,
            title,
          });
          priceLinesRef.current.push(line);
        });
      }
      const planLines = [
        ["Entry", tradePlan.entry, "#e2e8f0"],
        ["Stop", tradePlan.stop, "#ef4444"],
        ["TP1", tradePlan.tp1, "#22c55e"],
      ];
      if (tradePlan.tp2 != null) {
        planLines.push(["TP2 (partial)", tradePlan.tp2, "#86efac"]);
      }
      planLines.forEach(([name, price, color]) => {
        if (price == null) return;
        const line = series.createPriceLine({
          price,
          color,
          lineWidth: 2,
          lineStyle: 0,
          axisLabelVisible: true,
          title: name,
        });
        priceLinesRef.current.push(line);
      });

      // Every confluence factor behind this trade (FVG, Order Block, VWAP,
      // Fib, etc.) plotted at its exact level so "where's the FVG on the
      // chart?" has a visible answer instead of just the text breakdown.
      (tradePlan.confluenceLevels || []).forEach((factor) => {
        if (factor.level == null) return;
        const shortTerm = factor.term.split(" (")[0];
        const line = series.createPriceLine({
          price: factor.level,
          color: "#a855f7",
          lineWidth: 1,
          lineStyle: 1,
          axisLabelVisible: true,
          title: `${factor.timeframe} ${shortTerm}`,
        });
        priceLinesRef.current.push(line);
      });
    }
  }, []);

  // Marks the fresh entry (e.g. a manual "Scan For New Entry" result) with an
  // arrow directly on the most recent candle, so it's visually flagged on the
  // chart rather than just another price line among many.
  const applyMarker = useCallback((tradePlan) => {
    if (!markersRef.current) return;
    const candles = lastCandlesRef.current;
    if (tradePlan?.markerLabel && candles.length > 0) {
      const lastTime = candles[candles.length - 1].time;
      markersRef.current.setMarkers([{
        time: lastTime,
        position: tradePlan.bias === "SHORT" ? "aboveBar" : "belowBar",
        color: tradePlan.bias === "SHORT" ? "#f87171" : "#22d3ee",
        shape: tradePlan.bias === "SHORT" ? "arrowDown" : "arrowUp",
        text: tradePlan.markerLabel,
      }]);
    } else {
      markersRef.current.setMarkers([]);
    }
  }, []);

  const fetchChart = useCallback(async (tf) => {
    setLoading(true);
    try {
      let candles, zones, trade_plan, source, price_fetched_at;
      if (candleSource === "ict") {
        const res = await axios.get(`${API}/aura/intraday/candles`, { params: { symbol, timeframe: tf } });
        candles = res.data.candles;
        zones = {};
        trade_plan = null;
        source = res.data.source;
        price_fetched_at = null;
      } else {
        const res = await axios.get(`${API}/aura/chart/${asset}?timeframe=${tf}`);
        ({ candles, zones, trade_plan, source, price_fetched_at } = res.data);
      }
      const effectivePlan = overrideTradePlanRef.current || trade_plan;
      // Slice to the most recent 60 bars client-side before setData so candle
      // bodies always render wide and clearly readable (never squeezed thin).
      const visibleCandles = (candles || []).slice(-60);
      lastCandlesRef.current = visibleCandles;
      if (seriesRef.current) {
        seriesRef.current.setData(visibleCandles);
      }
      drawZonesAndPlan(zones, effectivePlan, activeZonesRef.current, showPlanRef.current);
      applyMarker(effectivePlan);
      setMeta({ source, price_fetched_at, zones, trade_plan: effectivePlan });
      if (chartRef.current) chartRef.current.timeScale().fitContent();
    } catch (error) {
      console.error("Error fetching chart:", error);
      toast.error(`Failed to load ${asset || symbol} chart`);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [asset, symbol, candleSource, drawZonesAndPlan, applyMarker]);

  useEffect(() => {
    if (ready) fetchChart(timeframe);
  }, [timeframe, fetchChart, ready]);

  // Redraw (no refetch) when the caller's override trade plan changes - e.g. a
  // fresh Trade of the Day/Week recommendation lands without a new candle fetch.
  useEffect(() => {
    overrideTradePlanRef.current = overrideTradePlan;
    if (meta) {
      const effectivePlan = overrideTradePlan || meta.trade_plan;
      drawZonesAndPlan(meta.zones, effectivePlan, activeZonesRef.current, showPlanRef.current);
      applyMarker(effectivePlan);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(overrideTradePlan)]);

  const toggleZone = (tf) => {
    const next = { ...activeZones, [tf]: !activeZones[tf] };
    setActiveZones(next);
    activeZonesRef.current = next;
    if (meta) drawZonesAndPlan(meta.zones, meta.trade_plan, next, showPlan);
  };

  const togglePlan = () => {
    const next = !showPlan;
    setShowPlan(next);
    showPlanRef.current = next;
    if (meta) drawZonesAndPlan(meta.zones, meta.trade_plan, activeZones, next);
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      if (candleSource !== "ict") {
        await axios.post(`${API}/aura/chart/${asset}/refresh`);
      }
      await fetchChart(timeframe);
      toast.success("Price data refreshed");
    } catch (error) {
      toast.error("Refresh failed");
    } finally {
      setRefreshing(false);
    }
  };

  const chartId = (asset || symbol || "chart").toLowerCase();
  const timeframeOptions = candleSource === "ict" ? ICT_TIMEFRAMES : TIMEFRAMES;

  return (
    <Card data-testid={`aura-chart-${chartId}`}>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <LineChart className="w-4 h-4" /> {label || asset || symbol} Chart
        </CardTitle>
        <div className="flex items-center gap-2">
          <div className="flex rounded-md border border-border overflow-hidden">
            {timeframeOptions.map((tf) => (
              <button
                key={tf.key}
                data-testid={`chart-timeframe-${tf.key}`}
                onClick={() => setTimeframe(tf.key)}
                className={`px-3 py-1 text-xs font-medium transition-colors ${
                  timeframe === tf.key ? "bg-primary text-primary-foreground" : "bg-transparent hover:bg-muted"
                }`}
              >
                {tf.label}
              </button>
            ))}
          </div>
          <Button
            size="sm"
            variant="outline"
            onClick={handleRefresh}
            disabled={refreshing}
            data-testid={`chart-refresh-${chartId}`}
          >
            <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? "animate-spin" : ""}`} />
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {chartError ? (
          <div
            className="flex flex-col items-center justify-center gap-2 py-16 text-muted-foreground"
            data-testid="chart-error-state"
          >
            <AlertTriangle className="w-6 h-6 text-amber-500" />
            <p className="text-sm">{chartError}</p>
          </div>
        ) : (
          <>
            <div className="relative">
              {(loading || !ready) && (
                <div className="absolute inset-0 flex items-center justify-center bg-background/50 z-10 rounded" style={{ minHeight: 340 }}>
                  <Loader2 className="w-6 h-6 animate-spin text-primary" />
                </div>
              )}
              <div ref={containerRef} data-testid="chart-canvas" style={{ minHeight: 340 }} />
            </div>
            <div className="flex flex-wrap items-center justify-between gap-2 mt-3">
              <div className="flex flex-wrap items-center gap-1.5">
                {showZoneToggles && (
                  <>
                    <span className="text-xs text-muted-foreground mr-1">Zones:</span>
                    {Object.entries(ZONE_STYLE).map(([tf, style]) => (
                      <button
                        key={tf}
                        data-testid={`chart-zone-toggle-${tf}`}
                onClick={() => toggleZone(tf)}
                className={`flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium border transition-all ${
                  activeZones[tf]
                    ? "border-transparent text-white"
                    : "border-border text-muted-foreground opacity-50 hover:opacity-80"
                }`}
                style={activeZones[tf] ? { backgroundColor: `${style.color}26`, color: style.color, borderColor: `${style.color}55` } : undefined}
              >
                <span className="w-2 h-2 rounded-full inline-block" style={{ background: style.color }} />
                {style.label}
              </button>
                    ))}
                  </>
                )}
            <button
              data-testid="chart-plan-toggle"
              onClick={togglePlan}
              className={`flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium border transition-all ${
                showPlan
                  ? "border-emerald-500/40 bg-emerald-500/15 text-emerald-400"
                  : "border-border text-muted-foreground opacity-50 hover:opacity-80"
              }`}
            >
              <span className="w-2 h-2 rounded-full inline-block bg-emerald-400" />
              Trade Plan
            </button>
          </div>
          {meta?.source && (
            <span className="text-xs text-muted-foreground" data-testid="chart-source-label">
              {candleSource === "ict" ? `Live yfinance (${meta.source})` : meta.source === "alpha_vantage" ? "Alpha Vantage" : "yfinance (fallback)"}
              {meta.price_fetched_at && ` · ${new Date(meta.price_fetched_at).toLocaleTimeString()}`}
            </span>
          )}
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default AuraLightweightChart;
